import { useEffect, useRef, useState } from 'react';
import { FiChevronDown, FiVideo } from 'react-icons/fi';
import { usePassportKioskPartnerControl } from '../hooks/usePassportKioskPartnerControl';
import './PassportKioskHeaderControl.css';

export default function PassportKioskHeaderControl() {
  const {
    scanEnabled,
    idleVideoUrl,
    loading,
    busy,
    videoBusy,
    error,
    videoNotice,
    fileRef,
    toggleScan,
    onPickVideo,
    onRemoveVideo,
    openFilePicker,
  } = usePassportKioskPartnerControl();

  const [videoOpen, setVideoOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!videoOpen) return undefined;
    const onDoc = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setVideoOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [videoOpen]);

  const statusShort = loading
    ? '…'
    : scanEnabled
      ? 'Scan enabled'
      : 'Scan paused';

  return (
    <div
      className={`passport-header-control${scanEnabled ? ' passport-header-control--on' : ''}`}
      aria-label="Passport kiosk camera"
    >
      <div className="passport-header-control__scan">
        <span className="passport-header-control__label">Passport</span>
        <span
          className={`passport-header-control__status${scanEnabled ? ' is-on' : ''}`}
          aria-live="polite"
        >
          <span className="passport-header-control__dot" aria-hidden />
          {statusShort}
        </span>
        <button
          type="button"
          className={`passport-header-control__toggle${scanEnabled ? ' is-stop' : ' is-start'}`}
          onClick={toggleScan}
          disabled={loading || busy}
        >
          {busy ? '…' : scanEnabled ? 'Pause scan' : 'Enable scan'}
        </button>
      </div>

      <div className="passport-header-control__video-wrap" ref={panelRef}>
        <button
          type="button"
          className={`passport-header-control__video-btn${videoOpen ? ' is-open' : ''}`}
          aria-expanded={videoOpen}
          aria-haspopup="dialog"
          onClick={() => setVideoOpen((v) => !v)}
          disabled={loading}
          title="Idle loop video for Passport kiosk"
        >
          <FiVideo aria-hidden />
          <span className="passport-header-control__video-btn-label">Idle video</span>
          <FiChevronDown className="passport-header-control__chevron" aria-hidden />
        </button>

        {videoOpen ? (
          <div className="passport-header-control__panel" role="dialog" aria-label="Passport idle video">
            <p className="passport-header-control__panel-title">Idle loop video</p>
            <p className="passport-header-control__panel-hint">Shown on Passport while guest scan is paused.</p>
            <div className="passport-header-control__preview-wrap">
              {idleVideoUrl ? (
                <video
                  className="passport-header-control__preview"
                  src={idleVideoUrl}
                  muted
                  loop
                  playsInline
                  controls
                  preload="metadata"
                />
              ) : (
                <p className="passport-header-control__empty">No video uploaded</p>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v"
              className="passport-header-control__file"
              onChange={onPickVideo}
            />
            <div className="passport-header-control__panel-actions">
              <button
                type="button"
                className="passport-header-control__panel-btn"
                disabled={videoBusy}
                onClick={openFilePicker}
              >
                {videoBusy ? 'Uploading…' : idleVideoUrl ? 'Replace' : 'Upload'}
              </button>
              {idleVideoUrl ? (
                <button
                  type="button"
                  className="passport-header-control__panel-btn passport-header-control__panel-btn--ghost"
                  disabled={videoBusy}
                  onClick={onRemoveVideo}
                >
                  Remove
                </button>
              ) : null}
            </div>
            {videoNotice ? <p className="passport-header-control__notice">{videoNotice}</p> : null}
            {error ? <p className="passport-header-control__error">{error}</p> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
